import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ClearIcon, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Box } from '@mui/system';
import { IconButton } from '@mui/material';
import { PickerValue } from '@mui/x-date-pickers/internals';
import { useEffect, useState } from 'react';

dayjs.extend(utc);

interface Props {
  label: string;
  defaultValue?: string | undefined | null;
  onDatePick?: (date: string | undefined) => void;
  allowCancel?: string;
  bindError?: () => { error?: boolean; helperText?: string };
  disableKeyDown?: boolean; //prevents from manual date input
}

export const InputDateTimePicker = ({
  defaultValue,
  label,
  onDatePick,
  allowCancel,
  bindError,
  disableKeyDown,
}: Props) => {
  const [value, setValue] = useState<Dayjs | null>(null);

  //sets value and converts it from UTC to local time if it's valid
  useEffect(() => {
    if (defaultValue && dayjs.utc(defaultValue).isValid()) {
      setValue(dayjs.utc(defaultValue).local());
    }
  }, [defaultValue]);

  const handleChange = (date: PickerValue) => {
    setValue(date);
    onDatePick?.(date?.utc().format('YYYY-MM-DDTHH:mm'));
  };

  const preventKeydown = (e: React.KeyboardEvent<HTMLElement>) => e.preventDefault();

  const clearValue = () => onDatePick?.(undefined);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box display="flex" alignItems="center" gap={2} height={'100%'}>
        <DateTimePicker
          label={label}
          value={value}
          format="DD-MM-YYYY HH:mm"
          ampm={false} //24h format
          onChange={handleChange}
          sx={{ width: '100%' }}
          slotProps={{
            textField: {
              ...bindError?.(), //useForm error handler
              InputProps: {
                readOnly: disableKeyDown,
                onKeyDown: disableKeyDown ? preventKeydown : undefined, //prevents every keyboard input
              },
              InputLabelProps: { shrink: true },
            },
          }}
        />
        {allowCancel && (
          <IconButton onClick={clearValue}>
            <ClearIcon />
          </IconButton>
        )}
      </Box>
    </LocalizationProvider>
  );
};
